import { Trans, useLingui } from '@lingui/react/macro';
import { useState, useEffect, useCallback, useRef } from 'react';
import { LockOutlined } from '@ant-design/icons';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import ThreadPanelHeader from './ThreadPanelHeader';
import ActivityBar from './ActivityBar';
import NormalizedConversation from './NormalizedConversation';
import { useNormalizedSessionScope, useProviderOutputMode } from '../hooks/useNormalizedSessionScope';
import { useChannelMembers } from '../hooks/useChannelMembers';
import { UserAvatar } from './UserAvatar';
import { useAuthStore, useThreadPanelStore, useChannelStore, useChannelMemberStore, useMessageStore } from '../stores';
import { useTaskStore } from '../stores/useTaskStore';
import { dispatchOutgoingMessage } from '../stores/actions';
import { pendingThreadDraftKey, threadDraftKey } from '../hooks/useComposerDraft';
import * as api from '../lib/api';
import { toUploadFile } from '../hosts/web/files';
import { routes } from '../lib/routes';
import type { Task } from '../lib/api';
import type { Message } from '../types';

const EMPTY_MESSAGES: Message[] = [];

type Props =
  | { threadId: string; onClose: () => void; pendingMessageId?: undefined; channelId?: undefined; onThreadCreated?: undefined }
  | { threadId?: undefined; pendingMessageId: string; channelId: string; onClose: () => void; onThreadCreated: (threadId: string) => void };

export default function ThreadPanel(props: Props) {
  if (props.threadId) {
    return <ExistingThreadPanel threadId={props.threadId} onClose={props.onClose} />;
  }
  return (
    <PendingThreadPanel
      messageId={props.pendingMessageId!}
      channelId={props.channelId!}
      onClose={props.onClose}
      onThreadCreated={props.onThreadCreated!}
    />
  );
}

function ExistingThreadPanel({ threadId, onClose }: { threadId: string; onClose: () => void }) {
  const accountId = useAuthStore(s => s.currentUser?.id);
  const guildId = useAuthStore(s => s.guildId);
  const prepareThreadRoster = useChannelMemberStore(s => s.prepareThreadRoster);
  const threadDescriptor = useChannelMemberStore(s => s.threadChannelsById[threadId]);
  const membersVersion = useChannelStore(s => s.channelMembersVersion[threadId] ?? 0);
  const parentMembership = useChannelStore(s => s.channels.find(channel => channel.id === threadDescriptor?.parent_id)?.is_member);
  const [rosterRefresh, setRosterRefresh] = useState(0);
  const observedParent = useRef<{ scope: string; membership: boolean | undefined } | null>(null);
  const loadMessages = useMessageStore(s => s.loadMessages);
  const [resolvedTask, setResolvedTask] = useState<Task | null>(null);
  const upsertTask = useTaskStore(s => s.upsertTask);
  const task = useTaskStore(s => resolvedTask
    ? s.byTaskId[resolvedTask.id] ?? resolvedTask
    : undefined);
  const [threadName, setThreadName] = useState<string | null>(null);
  const loadChannels = useChannelStore(s => s.loadChannels);
  const parentChannel = useChannelStore(s => task
    ? s.channels.find(channel => channel.id === task.channel_id)
    : undefined);
  const isReadOnlyPublicThread = !!task && parentChannel?.visibility === 'public' && parentChannel.is_member === false;
  const [joining, setJoining] = useState(false);
  const accessKey = JSON.stringify([accountId, guildId, threadId]);
  const [readableScope, setReadableScope] = useState<string | null>(null);
  const { t } = useLingui();
  const { members: threadMembers, status: rosterStatus } = useChannelMembers(threadId);
  const readableAssignee = rosterStatus === 'ready' && readableScope === accessKey && task?.thread_id === threadId
    ? threadMembers.find(member => member.user_id === task.assignee_id && member.role === 'agent')
    : undefined;
  const outputMode = useProviderOutputMode(readableAssignee);
  const assigneeId = readableAssignee?.user_id;
  const assigneeScope = useNormalizedSessionScope(threadId, assigneeId);
  const setSessionView = useThreadPanelStore(state => state.setSessionView);
  const isTaskThread = task?.thread_id === threadId;
  const handleSessionViewChange = useCallback((selected: boolean) => {
    setSessionView(accessKey, isTaskThread && selected);
  }, [accessKey, isTaskThread, setSessionView]);
  useEffect(() => {
    const scope = JSON.stringify([accountId, guildId, threadId, threadDescriptor?.parent_id]);
    if (!threadDescriptor) {
      observedParent.current = null;
      return;
    }
    const previous = observedParent.current;
    observedParent.current = { scope, membership: parentMembership };
    if (previous?.scope === scope && previous.membership !== parentMembership) {
      setRosterRefresh(value => value + 1);
    }
  }, [accountId, guildId, threadId, threadDescriptor, parentMembership]);

  useEffect(() => {
    let cancelled = false;
    setReadableScope(null);
    const registerRoster = prepareThreadRoster(threadId);
    void api.getChannel(threadId).then(({ channel: thread, members }) => {
      const auth = useAuthStore.getState();
      if (cancelled || auth.currentUser?.id !== accountId || auth.guildId !== guildId) return;
      const registration = registerRoster(thread, members);
      if (registration === 'discarded') return;
      if (registration === 'stale') {
        setRosterRefresh(value => value + 1);
        return;
      }
      setReadableScope(JSON.stringify([accountId, guildId, threadId]));
      setThreadName(thread.name);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [threadId, accountId, guildId, membersVersion, rosterRefresh, prepareThreadRoster]);

  useEffect(() => {
    let cancelled = false;
    setResolvedTask(null);
    void api.getTaskByThread(threadId).then((resolved) => {
      if (cancelled) return;
      setResolvedTask(resolved);
      upsertTask(resolved);
    }).catch(() => {
      // A non-task Thread or an inaccessible Task stays task-free. The server
      // makes both cases non-disclosing through the same lookup contract.
    });
    return () => { cancelled = true; };
  }, [threadId, accountId, guildId, upsertTask]);


  useEffect(() => {
    loadMessages(threadId);
  }, [threadId, loadMessages]);

  const handleJoin = useCallback(async () => {
    if (!task || joining) return;
    setJoining(true);
    try {
      await api.joinChannel(task.channel_id);
      await loadChannels();
    } finally {
      setJoining(false);
    }
  }, [joining, loadChannels, task]);

  return (
    <div className="thread-panel">
      <ThreadPanelHeader
        title={threadName || t`Thread`}
        onClose={onClose}
        task={task}
        assigneeName={task?.assignee?.display_name}
        parentChannelRoute={task?.thread_id
          ? `${routes.thread(task.guild_id, task.channel_id, task.thread_id)}?tab=chat`
          : undefined}
        conversationId={threadId}
        sessionLayoutOwner={accessKey}
      />
      <NormalizedConversation onSessionViewChange={handleSessionViewChange} scope={assigneeScope} outputMode={outputMode} surface="conversation" agentName={readableAssignee?.display_name}
        canWrite={!isReadOnlyPublicThread} defaultView="session" draftKey={threadDraftKey(threadId)} composerClassName="thread-panel-input"
        messages={<>
          <div className="thread-panel-messages"><MessageList channelId={threadId} /></div>
          <ActivityBar channelId={threadId} />
        </>}
        composer={isReadOnlyPublicThread ? (
          <div className="readonly-composer-placeholder" role="status">
            <LockOutlined aria-hidden="true" />
            <span><Trans>Read-only</Trans></span>
            <button onClick={handleJoin} disabled={joining}>
              {joining ? t`Joining…` : parentChannel ? t`Join #${parentChannel.name} to reply` : t`Join the channel to reply`}
            </button>
          </div>
        ) : <MessageInput channelId={threadId} draftKey={threadDraftKey(threadId)} />}
      />
    </div>
  );
}

function PendingThreadPanel({ messageId, channelId, onClose, onThreadCreated }: {
  messageId: string;
  channelId: string;
  onClose: () => void;
  onThreadCreated: (threadId: string) => void;
}) {
  const { t } = useLingui();
  const messages = useMessageStore(s => s.messages[channelId]) ?? EMPTY_MESSAGES;

  const originMessage = messages.find(m => m.id === messageId);
  const senderName = originMessage?.sender_name ?? t`Unknown`;

  // The thread is only persisted by this first reply, so a failed creation has
  // to reject rather than be swallowed: the composer keeps the typed text and
  // its stored draft, and the member can send again. Once the thread exists the
  // reply is an ordinary message in an ordinary channel, so it goes out on the
  // same optimistic rail as every reply after it — a pending row in the thread
  // that turns into a failed one the member can retry if no ack arrives, rather
  // than a frame handed to a socket that may not be open.
  const handleSend = useCallback(async (content: string, mentions: string[], attachments: File[]) => {
    const threadName = (originMessage?.content || t`Thread`).slice(0, 30);
    const thread = await api.createThreadFromMessage(channelId, messageId, threadName);
    if (attachments.length > 0) {
      await api.sendMessageWithAttachments(
        thread.id,
        content,
        await Promise.all(attachments.map(toUploadFile)),
      );
    } else {
      dispatchOutgoingMessage({ channelId: thread.id, content, contentType: 'text', mentions });
    }
    onThreadCreated(thread.id);
  }, [originMessage, channelId, messageId, onThreadCreated, t]);

  return (
    <div className="thread-panel">
      <ThreadPanelHeader title={t`New thread`} onClose={onClose} />
      <div className="thread-panel-messages thread-panel-pending">
        {originMessage && (
          <div className="thread-origin-message">
            <div className="message-item">
              <UserAvatar userId={originMessage.sender_id} displayName={senderName} size={36} />
              <div className="message-body">
                <div className="message-header">
                  <span className="message-sender">{senderName}</span>
                </div>
                <div className="message-text">{originMessage.content}</div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="thread-panel-input">
        <MessageInput
          channelId={channelId}
          draftKey={pendingThreadDraftKey(messageId)}
          onSendOverride={handleSend}
        />
      </div>
    </div>
  );
}
